import { Tender, TenderType } from "@/graphql/__generated__/graphql";
import { formatDateLocale, formatNumberLocale, getProvinceNameByCode } from "@/utils/helpers";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { IntlShape, useIntl } from "react-intl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import UpdateCustomerNameDialog from "../Components/UpdateCustomerNameDialog";

// Keys to exclude and not render
const excludedKeys = ["__typename", "ownerId", "customerId", "owner"];
// Keys to render as currency
const currencyKeys = ["revenue"];
// Keys to render as date
const dateKeys = ["publication", "submission", "questions", "outsideInformation", "siteVisit", "startDate", "endDate"];
// Keys to render as optional date, if value is null, it will display as N/A
const optionalDateKeys = ["publication", "questions", "outsideInformation", "siteVisit"];
// Keys to render as percentage
const percentageKeys = ["margin", "escalationClause", "priceCriteria", "qualityCriteria", "referenceCriteria"];
// Keys to render as hours by adding "hours" to the value
const hoursKey = ["effort", "hoursPerMonth"];
// Keys to render as type locales, render the enum values as the value in the locales in common:{type},
// requires to have the locales in translations.
const typeLocalesKey = ["type", "customerType"];

/**
 * Render the field value
 * @param t - The translation function
 * @param intl - The intl shape
 * @param key - The key
 * @param value - The value
 * @param tenderType - The tender type
 * @returns The rendered field value
 */
const renderField = (t: TFunction, intl: IntlShape, key: string, value: any, tenderType: TenderType) => {
  if (tenderType === TenderType.QuickOffer && (!value || value === null)) {
    return null;
  }
  if (dateKeys.includes(key)) {
    if (optionalDateKeys.includes(key) && value === null) {
      return t("common:notavailable");
    }
    return formatDateLocale(intl, value);
  }
  if (currencyKeys.includes(key)) {
    return formatNumberLocale(intl, value, "currency");
  }
  if (percentageKeys.includes(key)) {
    if (value === null) {
      return t("common:notavailable");
    };
    return `${parseInt(value)/100}%`;
  }
  if (hoursKey.includes(key)) {
    return `${value} hours`;
  }
  if (typeLocalesKey.includes(key)) {
    if (key === "type") {
      return t(`common:tendertype.${value.toLowerCase()}`);
    }
    return t(`common:${key.toLowerCase()}.${value.toLowerCase()}`);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (key === "province") {
    return getProvinceNameByCode(value);
  };
  if (!value || value === null) {
    return null;
  }

  return String(value);
};

/**
 * Render the details row
 * @param key - The key
 * @param title - The title
 * @param value - The value
 * @returns The rendered details row
 */
const renderDetailsRow = (key: string, title: string, value: string | null) => {
  if (value) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 hover:bg-slate-50 rounded-md">
        <p className="w-1/3 text-slate-500">{title}</p>
        <div className="w-2/3 flex justify-between items-center">
          {value}
          {
            key === "customerName" && (
              <UpdateCustomerNameDialog />
            )
          }
        </div>
      </div>
    );
  }
  return null;
};

/**
 * Render the tender detail
 * @param t - The translation function
 * @param intl - The intl shape
 * @param key - The key
 * @param value - The value
 * @param tenderType - The tender type
 * @returns The rendered tender detail
 */
const renderTenderDetail = (t: TFunction,intl: IntlShape, key: string, value: any, tenderType: TenderType) => (
  <Card key={key}>
    <CardHeader className="pt-4 pb-2 select-none">
      <CardTitle className="text-lg">{t(`edittender.details.formgroups.${key.toLowerCase()}.title`) ?? ""}</CardTitle>
    </CardHeader>
    <CardContent className="p-2">
      <div className="flex flex-col text-sm">
        {value && typeof value === "object" ? (
          Object.entries(value).map(([subKey, subValue]) => (
            !excludedKeys.includes(subKey) && (
              <div key={subKey}>
                {renderDetailsRow(
                  subKey,
                  t(`edittender.details.formgroups.${key.toLowerCase()}.labels.${subKey.toLowerCase()}`) ?? "",
                  renderField(t, intl, subKey, subValue, tenderType)
                )}
              </div>
            )
          ))
        ) : (
          !excludedKeys.includes(key) && (
            <div key={key}>
              {renderDetailsRow(
                key,
                t(`edittender.details.formgroups.${key.toLowerCase()}.labels.${key.toLowerCase()}`) ?? "",
                renderField(t, intl, key, value, tenderType)
              )}
            </div>
          )
        )}
      </div>
    </CardContent>
  </Card>
);

/**
 * DetailsViewComponent renders the tender details in a view mode
 * @param tenderData - The tender data
 * @returns The rendered tender details
 */
function DetailsViewComponent({ tenderData }: { tenderData: Tender }) {
  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["translation", "common"]);

  const { description, deadline, estimate, contract, customer } = tenderData || {};

  const tenderType = description?.type;

  const tenderDetails = {
    description: {
      ...description,
      customerName: customer?.name
    },
    deadline,
    ...(description?.type !== TenderType.QuickOffer && { estimate }),
    contract,
  } as Partial<Tender>;

  return (
    <div className="flex flex-col gap-4">
      {/* Tender details cards */}
      {Object.entries(tenderDetails).map(([key, value]) => renderTenderDetail(t, intl, key, value, tenderType))}
    </div>
  );
}

export default DetailsViewComponent;
